// Process Service
// Complete CRUD operations for Processes
// Part of Policy Framework & EU Diseases Implementation

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import auditService from './auditService';

class ProcessService {
  constructor() {
    this.collectionName = 'processes';
    this.collectionRef = collection(db, this.collectionName);
  }

  /**
   * Get all processes for an organization
   */
  async getProcesses(organizationId, filters = {}) {
    try {
      let q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        where('isActive', '==', true)
      );

      // Apply filters
      if (filters.policyFrameworkId) {
        q = query(q, where('policyFrameworkId', '==', filters.policyFrameworkId));
      }

      if (filters.processCategory) {
        q = query(q, where('processCategory', '==', filters.processCategory));
      }

      if (filters.isIPCRelevant !== undefined) {
        q = query(q, where('isIPCRelevant', '==', filters.isIPCRelevant));
      }

      // Order by process code
      q = query(q, orderBy('processCode', 'asc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting processes:', error);
      throw error;
    }
  }

  /**
   * Get single process
   */
  async getProcess(processId) {
    try {
      const docRef = doc(this.collectionRef, processId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Process not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('Error getting process:', error);
      throw error;
    }
  }

  /**
   * Get standard templates (QMS Phase 1: processes with isTemplate true, organizationId null)
   */
  async getStandardTemplates(filters = {}) {
    try {
      let q = query(
        this.collectionRef,
        where('isTemplate', '==', true),
        where('isActive', '==', true),
        orderBy('processCode', 'asc')
      );
      if (filters.domain) {
        q = query(
          this.collectionRef,
          where('isTemplate', '==', true),
          where('isActive', '==', true),
          where('domain', '==', filters.domain),
          orderBy('processCode', 'asc')
        );
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error('Error getting standard templates:', error);
      throw error;
    }
  }

  /**
   * Create new process (or standard template when organizationId null and isTemplate true)
   */
  async createProcess(data, userId, userName) {
    try {
      const isTemplate = data.isTemplate === true;
      if (!isTemplate && !data.organizationId) {
        throw new Error('Organization ID is required for process instances');
      }
      if (!data.processCode) {
        throw new Error('Process code is required');
      }
      if (!data.processName) {
        throw new Error('Process name is required');
      }

      const now = Timestamp.now();

      const processData = {
        ...data,
        organizationId: data.organizationId ?? null,
        isTemplate: isTemplate,
        domain: data.domain ?? null,
        regulatorySource: data.regulatorySource ?? null,
        clonedFromId: data.clonedFromId ?? null,
        templateVersion: data.templateVersion ?? null,
        customNotes: data.customNotes ?? null,
        createdAt: now,
        createdBy: userId,
        createdByName: userName || userId,
        updatedAt: now,
        updatedBy: userId,
        updatedByName: userName || userId,
        isActive: true,
        status: data.status || 'active',
        version: data.version || '1.0',
        // Ensure arrays exist
        processSteps: data.processSteps || [],
        steps: data.steps || data.processSteps || [],
        linkedProtocols: data.linkedProtocols || [],
        processKPIs: data.processKPIs || [],
        kpis: data.kpis || data.processKPIs || {},
        responsibleDepartments: data.responsibleDepartments || [],
        tags: data.tags || []
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(processData);

      const docRef = await addDoc(this.collectionRef, cleanedData);
      try {
        auditService.logCreate('processes', docRef.id, data.processName || data.processCode || 'Process', { processCode: data.processCode });
      } catch (e) {
        console.warn('Audit log failed:', e);
      }
      return docRef.id;
    } catch (error) {
      console.error('Error creating process:', error);
      throw error;
    }
  }

  /**
   * Update process
   */
  async updateProcess(processId, updates, userId, userName) {
    try {
      const docRef = doc(this.collectionRef, processId);
      const current = await this.getProcess(processId).catch(() => null);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(updateData);

      await updateDoc(docRef, cleanedData);
      try {
        auditService.logUpdate('processes', processId, current?.processName || current?.processCode || processId, { changedFields: Object.keys(updates) });
      } catch (e) {
        console.warn('Audit log failed:', e);
      }
      return processId;
    } catch (error) {
      console.error('Error updating process:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) process
   * Now includes dependency checking - throws error if dependencies exist
   */
  async deleteProcess(processId, userId, userName, force = false) {
    // Check dependencies before deletion
    if (!force) {
      const { dependencyCheckService } = await import('./dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkProcessDeletion(processId);
      
      if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
        const errorMessages = dependencyCheck.blockingDependencies.map(d => d.message).join('; ');
        throw new Error(`Cannot delete process: ${errorMessages}`);
      }
    }

    try {
      const docRef = doc(this.collectionRef, processId);
      const current = await this.getProcess(processId).catch(() => null);
      
      await updateDoc(docRef, {
        isActive: false,
        status: 'archived',
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      });
      try {
        auditService.logDelete('processes', processId, current?.processName || current?.processCode || processId, { status: 'archived' });
      } catch (e) {
        console.warn('Audit log failed:', e);
      }
      return processId;
    } catch (error) {
      console.error('Error deleting process:', error);
      throw error;
    }
  }

  /**
   * Get processes by policy framework
   */
  async getProcessesByFramework(policyFrameworkId) {
    try {
      const q = query(
        this.collectionRef,
        where('policyFrameworkId', '==', policyFrameworkId),
        where('isActive', '==', true),
        orderBy('processCode', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting processes by framework:', error);
      throw error;
    }
  }

  /**
   * Get IPC-relevant processes
   */
  async getIPCProcesses(organizationId) {
    try {
      const q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId),
        where('isIPCRelevant', '==', true),
        where('isActive', '==', true),
        orderBy('processCode', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting IPC processes:', error);
      throw error;
    }
  }

  /**
   * Link protocol to process
   */
  async linkProtocol(processId, protocolId, protocolName, linkType, implementsStep, userId, userName) {
    try {
      const process = await this.getProcess(processId);
      
      const newLink = {
        protocolId,
        protocolName,
        implementsStep,
        linkType,
        linkedAt: new Date()
      };

      const updatedLinks = [...(process.linkedProtocols || []), newLink];

      await this.updateProcess(
        processId,
        { linkedProtocols: updatedLinks },
        userId,
        userName
      );
    } catch (error) {
      console.error('Error linking protocol:', error);
      throw error;
    }
  }

  /**
   * Unlink protocol from process
   */
  async unlinkProtocol(processId, protocolId, userId, userName) {
    try {
      const process = await this.getProcess(processId);
      
      const updatedLinks = (process.linkedProtocols || []).filter(
        link => link.protocolId !== protocolId
      );

      await this.updateProcess(
        processId,
        { linkedProtocols: updatedLinks },
        userId,
        userName
      );
    } catch (error) {
      console.error('Error unlinking protocol:', error);
      throw error;
    }
  }

  /**
   * Update process KPI
   */
  async updateProcessKPI(processId, kpiId, newValue, userId, userName) {
    try {
      const process = await this.getProcess(processId);
      
      const updatedKPIs = (process.processKPIs || []).map(kpi => {
        if (kpi.kpiId === kpiId) {
          return {
            ...kpi,
            currentValue: newValue,
            lastUpdated: new Date(),
            status: this.determineKPIStatus(newValue, kpi.target)
          };
        }
        return kpi;
      });

      await this.updateProcess(
        processId,
        { processKPIs: updatedKPIs },
        userId,
        userName
      );
    } catch (error) {
      console.error('Error updating process KPI:', error);
      throw error;
    }
  }

  /**
   * Helper: Determine KPI status
   */
  determineKPIStatus(currentValue, target) {
    if (!target || !currentValue) return 'on-target';
    
    if (target.includes('>')) {
      const targetValue = parseFloat(target.replace(/[^0-9.]/g, ''));
      return currentValue >= targetValue ? 'on-target' : 'below-target';
    } else if (target.includes('<')) {
      const targetValue = parseFloat(target.replace(/[^0-9.]/g, ''));
      return currentValue <= targetValue ? 'on-target' : 'above-target';
    }
    return 'on-target';
  }

  /**
   * Get process statistics
   */
  async getProcessStatistics(organizationId) {
    try {
      const allProcesses = await this.getProcesses(organizationId);
      
      return {
        total: allProcesses.length,
        active: allProcesses.filter(p => p.status === 'active').length,
        underReview: allProcesses.filter(p => p.status === 'under-review').length,
        ipcRelevant: allProcesses.filter(p => p.isIPCRelevant).length,
        byCategory: this.groupByCategory(allProcesses)
      };
    } catch (error) {
      console.error('Error getting process statistics:', error);
      throw error;
    }
  }

  /**
   * Helper: Group processes by category
   */
  groupByCategory(processes) {
    return processes.reduce((acc, process) => {
      const category = process.processCategory || 'Uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Helper: Remove undefined values from object
   */
  removeUndefinedValues(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
}

export default new ProcessService();

